/**
 * PupRing Custom JavaScript - Upsells Module
 * ===========================================
 * Handles upsell product functionality in cart
 *
 * Features:
 * - UPSELL_VARIANT_IDS constant for exclusion
 * - window.getEligibleItemCount() for counting non-upsell items
 * - window.addToCartUpsell() for adding upsell products
 * - UpsellQuantityManager for dynamic button text updates
 *
 * Dependencies: None (provides global functions for other modules)
 */

/* ==================================
   Upsell System
   ================================== */

// Upsell variant IDs to exclude from eligible item counting
// Update this array if you change upsell products in theme settings
const UPSELL_VARIANT_IDS = [
  34054986596483,  // Warranty/Protection
  34323290521731,  // Gift Box
  43449192841347,  // Engraving
  43636224491651,  // Gift Message
  44421842534531   // Additional Upsell
];

// Shared function to count eligible items (excluding upsells)
// Used by cart goals and upsell quantity suggestions
window.getEligibleItemCount = async function() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    let count = 0;
    cart.items.forEach(item => {
      if (!UPSELL_VARIANT_IDS.includes(item.variant_id)) {
        count += item.quantity;
      }
    });

    return count;
  } catch (error) {
    return 1; // Default to 1 on error
  }
};

// Helper function for adding upsell products
// Supports dynamic quantity via data-quantity attribute
window.addToCartUpsell = function(button) {
  const variantId = button.dataset.variantId;
  if (!variantId) return;

  // Support dynamic quantity (default to 1)
  const quantity = parseInt(button.dataset.quantity) || 1;

  // Store original text for reset
  const originalText = button.textContent;

  // Add loading state
  button.disabled = true;
  button.textContent = 'Adding...';

  // Add to cart via AJAX
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      id: variantId,
      quantity: quantity
    })
  })
  .then(response => {
    // Check if the request was successful
    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    // Add loading class for visual feedback
    button.classList.add('btn--loading');

    // Pass the Response object to refreshCartContents (NOT parsed JSON)
    if (window.refreshCartContents) {
      window.refreshCartContents(response);
    }

    // Reset button after a short delay
    setTimeout(() => {
      button.classList.remove('btn--loading');
      button.textContent = originalText;
      button.disabled = false;
    }, 500);
  })
  .catch(error => {
    // Error adding to cart
    button.classList.remove('btn--loading');
    button.textContent = originalText;
    button.disabled = false;
  });
};

/**
 * UpsellQuantityManager
 * Updates upsell button text based on eligible item count
 * Shows "Add 2" when customer has 2+ items in cart
 * Applies to: Gift Boxes, Lifetime Warranty (per unique item)
 */
class UpsellQuantityManager {
  static async updateButtons() {
    const count = await window.getEligibleItemCount();

    // Update Gift Box buttons
    const giftBoxButtons = document.querySelectorAll('.gift-box-add');
    giftBoxButtons.forEach(button => {
      // Don't update if button is in loading state
      if (button.disabled || button.classList.contains('btn--loading')) return;

      if (count > 1) {
        button.textContent = `+ Add ${count}`;
        button.dataset.quantity = count;
      } else {
        button.textContent = '+ Add';
        button.dataset.quantity = 1;
      }
    });

    // Update Lifetime Warranty buttons (same logic - per unique item)
    const warrantyButtons = document.querySelectorAll('.warranty-add');
    warrantyButtons.forEach(button => {
      // Don't update if button is in loading state
      if (button.disabled || button.classList.contains('btn--loading')) return;

      if (count > 1) {
        button.textContent = `+ Add ${count}`;
        button.dataset.quantity = count;
      } else {
        button.textContent = '+ Add';
        button.dataset.quantity = 1;
      }
    });
  }

  static init() {
    // Initial update
    UpsellQuantityManager.updateButtons();

    // Update on cart changes
    window.addEventListener('updated.ecomify.cart', () => {
      // Small delay to ensure cart data is fresh
      setTimeout(() => {
        UpsellQuantityManager.updateButtons();
      }, 100);
    });
  }
}

// Initialize UpsellQuantityManager
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    UpsellQuantityManager.init();
  });
} else {
  UpsellQuantityManager.init();
}

// Export for use by other modules
window.UPSELL_VARIANT_IDS = UPSELL_VARIANT_IDS;
window.UpsellQuantityManager = UpsellQuantityManager;
